<template>
  <app-layout title="Detail Pengumuman Pemenang">
    <template #header>
      <div class="flex items-center justify-between">
        <h2 class="font-semibold text-xl text-gray-800 leading-tight">
          Detail Pengumuman Pemenang
        </h2>
        <button
          @click="goBack"
          class="inline-flex items-center px-4 py-2 bg-gray-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-gray-700 focus:outline-none focus:border-gray-700 focus:ring focus:ring-gray-200 active:bg-gray-600 disabled:opacity-25 transition"
        >
          <svg class="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Kembali
        </button>
      </div>
    </template>

    <div class="py-12">
      <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
        <!-- Winner Announcement Header -->
        <div class="bg-gradient-to-r from-green-50 to-yellow-50 border border-green-200 rounded-lg p-6 mb-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <i class="fas fa-trophy text-yellow-500 text-3xl"></i>
            </div>
            <div class="ml-4">
              <h3 class="text-lg font-medium text-green-800">Pengumuman Pemenang</h3>
              <p class="text-sm text-green-700">
                Pemenang telah ditetapkan dan pengumuman sedang berlangsung
              </p>
              <div class="mt-2">
                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <i class="fas fa-check-circle mr-1"></i>
                  Status: Pengumuman Pemenang
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Document Information -->
        <div class="bg-white overflow-hidden shadow-xl sm:rounded-lg p-6 mb-6">
          <h3 class="text-lg font-medium text-gray-900 mb-4">Informasi Dokumen Pengadaan</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700">Nama Paket</label>
              <p class="mt-1 text-sm text-gray-900">{{ dokumen.paket_pengadaan?.nama_paket_pengadaan || '-' }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Nomor BAHP</label>
              <p class="mt-1 text-sm text-gray-900">{{ dokumen.nomor_bahp || '-' }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Tanggal BAHP</label>
              <p class="mt-1 text-sm text-gray-900">{{ formatDate(dokumen.tanggal_bahp) || '-' }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Status</label>
              <p class="mt-1 text-sm text-gray-900">{{ dokumen.tahap?.nama_tahap || '-' }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Status Dokumen</label>
              <p class="mt-1 text-sm">
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  :class="dokumen.status === 'selesai' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'"
                >
                  <i v-if="dokumen.status === 'selesai'" class="pi pi-check-circle mr-1"></i>
                  {{ (dokumen.status || '-').toUpperCase() }}
                </span>
              </p>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="mt-6 flex space-x-4">
            <Button 
              @click="showHPSModal"
              icon="pi pi-calculator"
              label="Lihat HPS"
              severity="info"
              size="small"
              text 
            />
            <Button 
              v-if="dokumen.nomor_baep && dokumen.tanggal_baep"
              @click="showBaepModal"
              icon="pi pi-file-pdf"
              label="Lihat BAEP"
              severity="success"
              size="small"
              text 
            />
            <Button 
              v-if="dokumen.nomor_bahp && dokumen.tanggal_bahp"
              @click="showBahpModal"
              icon="pi pi-file-pdf"
              label="Lihat BAHP"
              severity="success"
              size="small"
              text 
            />
            <Button 
              @click="goToSanggah"
              icon="pi pi-exclamation-triangle"
              label="Sanggah"
              severity="warning"
              size="small"
              text 
            />
            <Button 
              v-if="$page.props.session_role?.role?.name === 'pokja' && dokumen.status !== 'selesai' && allowSelesaikanNow"
              @click="openConfirmSelesai"
              icon="pi pi-check"
              label="Selesaikan"
              severity="success"
              size="small"
              text 
            />
          </div>

          <div class="mt-2 text-xs text-gray-600">
            Bisa diselesaikan per : 
            <span class="font-medium">
              {{ allowSelesaikanAt ? formatDateTime(allowSelesaikanAt) : '-' }}
            </span>
            (Setelah masa sanggah)
          </div>
        </div>

        <!-- Evaluasi Ulang History Alert -->
        <div v-if="evaluasiUlangHistory" class="bg-amber-50 border-l-4 border-amber-400 p-4 mb-6 rounded-lg shadow-sm">
          <div class="flex">
            <div class="flex-shrink-0">
              <i class="pi pi-history text-amber-400 text-xl"></i>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-amber-800">
                Sanggah Evaluasi Ulang Diterima
              </h3>
              <div class="mt-2 text-sm text-amber-700">
                <p>
                  Sanggah telah diterima dengan opsi <strong>Evaluasi Ulang</strong> pada 
                  <strong>{{ formatDateTime(evaluasiUlangHistory.created_at) }}</strong> 
                  oleh <strong>{{ evaluasiUlangHistory.sanggah?.dokumen_pengadaan_actor?.user?.fullname || '-' }}</strong>
                </p>
                <p class="mt-2">
                  <strong>Catatan:</strong> {{ evaluasiUlangHistory.sanggah?.keterangan_pembatalan || '-' }}
                </p>
                <div class="mt-3">
                  <a href="#" @click.prevent="viewEvaluasiUlangHistory" class="text-amber-800 underline hover:text-amber-900 font-medium">
                    Lihat Detail Riwayat Evaluasi →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Winner Information -->
        <div v-if="pemenang" class="bg-white overflow-hidden shadow-xl sm:rounded-lg p-6 mb-6">
          <h3 class="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <i class="fas fa-trophy text-yellow-500 mr-2"></i>
            Informasi Pemenang
          </h3>
          <div class="bg-green-50 border border-green-200 rounded-lg p-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-green-700">Nama Perusahaan</label>
                <p class="mt-1 text-lg font-semibold text-green-900">{{ pemenang.penyedia?.nama_perusahaan || '-' }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-green-700">Total Penawaran</label>
                <p class="mt-1 text-lg font-semibold text-green-900">{{ formatCurrency(getTotalNilaiPenawaran(pemenang)) }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-green-700">Tanggal Penetapan</label>
                <p class="mt-1 text-sm text-green-900">{{ formatDate(pemenang.tanggal_penetapan_pemenang) || '-' }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-green-700">Selisih dengan HPS</label>
                <p class="mt-1 text-sm font-medium" 
                   :class="getTotalSelisih(pemenang) > 0 ? 'text-red-600' : 'text-green-600'">
                  {{ formatCurrency(getTotalSelisih(pemenang)) }}
                  ({{ getTotalPercentageDiff(pemenang) > 0 ? '+' : '' }}{{ getTotalPercentageDiff(pemenang).toFixed(2) }}%)
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Pengajuan List -->
        <div class="bg-white overflow-hidden shadow-xl sm:rounded-lg p-6">
          <h3 class="text-lg font-medium text-gray-900 mb-4">Daftar Pengajuan</h3>
          
          <div v-if="pengajuan.length > 0">
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">No</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Penyedia</th>
                    <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Nilai Penawaran</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lulus / Tidak</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Keterangan</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="(item, index) in sortedPengajuan" :key="item.id"
                      :class="item.is_pemenang ? 'bg-green-50 border-l-4 border-green-400' : ''">
                    <td class="px-6 py-4 whitespace-nowrap text-sm">{{ index + 1 }}</td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="text-sm font-medium text-gray-900">
                        {{ item.penyedia?.nama_perusahaan || '-' }}
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {{ formatCurrency(getTotalNilaiPenawaran(item)) }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div v-if="item.penilaian_penyedia_dokumen_pengadaan && item.penilaian_penyedia_dokumen_pengadaan.length > 0" class="text-sm">
                        <div v-for="(penilaian, pidx) in item.penilaian_penyedia_dokumen_pengadaan" :key="pidx" class="mb-1">
                          <span :class="penilaian.is_lulus ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'" 
                                class="px-2 py-1 text-xs font-medium rounded-full">
                            {{ penilaian.is_lulus ? 'Lulus' : 'Tidak Lulus' }}
                          </span>
                          <span class="text-xs text-gray-500 ml-1">
                            {{ penilaian.bobot_penilaian_dokumen_pengadaan ? penilaian.bobot_penilaian_dokumen_pengadaan.nama : '' }}
                          </span>
                        </div>
                      </div>
                      <div v-else class="text-sm text-gray-500">-</div>
                    </td>
                    <td class="px-6 py-4">
                      <div class="text-sm text-gray-900">
                        {{ item.keterangan_penilaian || '-' }}
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span v-if="item.is_pemenang" class="inline-flex items-center text-green-600 font-semibold">
                        <i class="fas fa-trophy mr-1 text-yellow-500"></i>
                        PEMENANG
                      </span>
                      <span v-else class="text-gray-500">-</span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm">
                      <Button 
                        @click="showPenilaianDetail(item)"
                        icon="pi pi-eye"
                        label="Lihat Penilaian"
                        severity="info"
                        size="small"
                        text 
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div v-else class="text-center py-4 text-gray-500">
            Tidak ada pengajuan yang ditemukan
          </div>
        </div>
      </div>
    </div>

    <!-- HPS Modal -->
    <dialog-modal :show="hpsModalOpen" @close="closeHPSModal" :max-width="'7xl'">
      <template #title>
        HPS (Harga Perkiraan Sendiri)
      </template>

      <template #content>
        <div v-if="dokumen.detail_h_p_s_dokumen_pengadaan && dokumen.detail_h_p_s_dokumen_pengadaan.length > 0">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">No</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nama Barang/Jasa</th>
                  <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Volume</th>
                  <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Satuan</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Harga Satuan</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="(hps, index) in dokumen.detail_h_p_s_dokumen_pengadaan" :key="hps.id">
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ index + 1 }}</td>
                  <td class="px-6 py-4 text-sm text-gray-900">{{ hps.nama_barang_jasa }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">{{ hps.volume }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">{{ hps.satuan }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">{{ formatCurrency(hps.harga_satuan) }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900">{{ formatCurrency(hps.volume * hps.harga_satuan) }}</td>
                </tr>
                <tr class="bg-gray-50 font-semibold">
                  <td colspan="5" class="px-6 py-4 text-right text-sm text-gray-900">Total HPS:</td>
                  <td class="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                    {{ formatCurrency(getTotalHPS()) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-else class="text-center py-8 text-gray-500">
          Data HPS tidak tersedia
        </div>
      </template>

      <template #footer>
        <button
          @click="closeHPSModal"
          class="inline-flex items-center px-4 py-2 bg-gray-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-gray-700 focus:outline-none focus:border-gray-700 focus:ring focus:ring-gray-200 active:bg-gray-600 disabled:opacity-25 transition"
        >
          <svg class="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Kembali
        </button>
      </template>
    </dialog-modal>

    <!-- Detail Penilaian Modal -->
    <dialog-modal :show="penilaianModalOpen" @close="closePenilaianModal" :max-width="'7xl'">
      <template #title>
        Detail Penilaian - {{ selectedPengajuan?.penyedia?.nama_perusahaan }}
      </template>

      <template #content>
        <div v-if="selectedPengajuan" class="space-y-6">
          <!-- Perbandingan Harga -->
          <div>
            <h3 class="text-lg font-medium text-gray-900 mb-4">Perbandingan Harga</h3>
            <div class="bg-gray-50 p-4 rounded-lg">
              <div class="overflow-x-auto">
                <table class="min-w-full">
                  <thead>
                    <tr>
                      <th class="text-left text-sm font-medium text-gray-700 pb-3">Merk/Tipe/Specs</th>
                      <th class="text-left text-sm font-medium text-gray-700 pb-3">Nama Barang/Jasa</th>
                      <th class="text-right text-sm font-medium text-gray-700 pb-3">Volume</th>
                      <th class="text-right text-sm font-medium text-gray-700 pb-3">Harga Satuan</th>
                      <th class="text-right text-sm font-medium text-gray-700 pb-3">Total Penawaran</th>
                      <th class="text-right text-sm font-medium text-gray-700 pb-3">Total HPS</th>
                      <th class="text-right text-sm font-medium text-gray-700 pb-3">Selisih</th>
                      <th class="text-right text-sm font-medium text-gray-700 pb-3">Perbandingan</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(hps, index) in dokumen.detail_h_p_s_dokumen_pengadaan" :key="hps.id"
                        class="border-b border-gray-200">
                      <td class="py-2 text-sm text-gray-600">{{
                        selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index] ?
                        [
                          selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].merk,
                          selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].tipe,
                          selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].spesifikasi
                        ].filter(Boolean).join(' - ') || selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].uraian
                        : '-'
                      }}</td>
                      <td class="py-2 text-sm text-gray-600">{{ hps.nama_barang_jasa }}</td>
                      <td class="py-2 text-right text-sm font-medium">
                        {{ selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index]?.volume || '-' }}
                      </td>
                      <td class="py-2 text-right text-sm font-medium">
                        {{ selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index]
                          ? formatCurrency(selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].harga_satuan)
                          : '-' }}
                      </td>
                      <td class="py-2 text-right text-sm font-medium">
                        {{ selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index]
                          ? formatCurrency(selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].volume *
                                               selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].harga_satuan)
                          : '-' }}
                      </td>
                      <td class="py-2 text-right text-sm font-medium">{{ formatCurrency(hps.volume * hps.harga_satuan) }}</td>
                      <td class="py-2 text-right text-sm font-medium"
                          :class="selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index] &&
                                 (selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].volume *
                                  selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].harga_satuan) >
                                 (hps.volume * hps.harga_satuan)
                                 ? 'text-red-600' : 'text-green-600'">
                        {{ selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index]
                          ? formatCurrency((selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].volume *
                                                selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].harga_satuan) -
                                               (hps.volume * hps.harga_satuan))
                          : '-' }}
                      </td>
                      <td class="py-2 text-right text-sm font-medium">
                        <div v-if="selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index]"
                             class="flex items-center justify-end space-x-1"
                             :class="getPercentageDiff(
                               selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].volume *
                               selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].harga_satuan,
                               hps.volume * hps.harga_satuan
                             ) > 0 ? 'text-red-600' : 'text-green-600'">
                          <span class="font-medium">
                            {{ Math.abs(getPercentageDiff(
                              selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].volume *
                              selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].harga_satuan,
                              hps.volume * hps.harga_satuan
                            )).toFixed(2) }}%
                          </span>
                          <svg v-if="getPercentageDiff(
                            selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].volume *
                            selectedPengajuan.detail_penawaran_kepersertaan_pengadaan[index].harga_satuan,
                            hps.volume * hps.harga_satuan
                          ) > 0"
                            class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"></path>
                          </svg>
                          <svg v-else
                            class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                        </div>
                        <span v-else>-</span>
                      </td>
                    </tr>
                    <!-- Total Row -->
                    <tr class="border-t-2 border-gray-300 font-semibold">
                      <td class="py-3 text-sm text-gray-700">Total</td>
                      <td class="py-3 text-right text-sm">-</td>
                      <td class="py-3 text-right text-sm">-</td>
                      <td class="py-3 text-right text-sm">-</td>
                      <td class="py-3 text-right text-sm">
                        {{ formatCurrency(getTotalNilaiPenawaran(selectedPengajuan)) }}
                      </td>
                      <td class="py-3 text-right text-sm">
                        {{ formatCurrency(getTotalHPS()) }}
                      </td>
                      <td class="py-3 text-right text-sm"
                          :class="getTotalSelisih(selectedPengajuan) > 0 ? 'text-red-600' : 'text-green-600'">
                        {{ formatCurrency(getTotalSelisih(selectedPengajuan)) }}
                      </td>
                      <td class="py-3 text-right text-sm">
                        <div class="flex items-center justify-end space-x-1"
                             :class="getTotalPercentageDiff(selectedPengajuan) > 0 ? 'text-red-600' : 'text-green-600'">
                          <span class="font-medium">{{ Math.abs(getTotalPercentageDiff(selectedPengajuan)).toFixed(2) }}%</span>
                          <svg v-if="getTotalPercentageDiff(selectedPengajuan) > 0"
                            class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"></path>
                          </svg>
                          <svg v-else
                            class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Penilaian -->
          <div>
            <h3 class="text-lg font-medium text-gray-900 mb-4">Hasil Penilaian</h3>
            <div class="bg-gray-50 p-4 rounded-lg">
              <div v-if="selectedPengajuan.penilaian_penyedia_dokumen_pengadaan && selectedPengajuan.penilaian_penyedia_dokumen_pengadaan.length > 0">
                <div class="space-y-4">
                  <div v-for="penilaian in selectedPengajuan.penilaian_penyedia_dokumen_pengadaan" :key="penilaian.id"
                       class="border-b border-gray-200 pb-4">
                    <div class="flex items-center justify-between mb-2">
                      <label class="block text-sm font-medium text-gray-700">
                        {{ penilaian.bobot_penilaian?.jenis_bobot_penilaian?.nama_jenis_bobot || 
                           penilaian.bobot_penilaian?.kode_bobot?.replace(/_/g, ' ').replace(/\w\S*/g, function(txt) {
                             return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
                           }) || 'Kriteria Penilaian' }}
                        ({{ penilaian.bobot_penilaian?.persentase_bobot || 0 }}%)
                      </label>
                      <div class="flex items-center space-x-4">
                        <div class="flex items-center space-x-2">
                          <span class="text-sm font-medium">{{ penilaian.nilai }}</span>
                          <span class="text-sm text-gray-500">/ 100</span>
                        </div>
                        <div class="flex items-center">
                          <input type="checkbox"
                                 :checked="penilaian.is_lulus"
                                 disabled
                                 class="h-4 w-4 text-indigo-600 border-gray-300 rounded opacity-60">
                          <label class="ml-2 block text-sm text-gray-700">
                            {{ penilaian.is_lulus ? 'Lulus' : 'Tidak Lulus' }}
                          </label>
                        </div>
                      </div>
                    </div>
                    <div class="text-sm text-gray-500">
                      Nilai Akhir: {{ ((penilaian.nilai || 0) * (penilaian.bobot_penilaian?.persentase_bobot || 0) / 100).toFixed(2) }}
                    </div>
                  </div>

                  <!-- Total Score -->
                  <div class="pt-4 border-t-2 border-gray-300">
                    <div class="flex items-center justify-between">
                      <span class="text-lg font-medium text-gray-900">Total Nilai</span>
                      <span class="text-lg font-medium"
                            :class="getTotalNilaiPenilaian(selectedPengajuan) >= 60 ? 'text-green-600' : 'text-red-600'">
                        {{ getTotalNilaiPenilaian(selectedPengajuan).toFixed(2) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="text-center py-4 text-gray-500">
                Belum ada penilaian untuk pengajuan ini
              </div>
            </div>
          </div>

          <!-- Keterangan -->
          <div v-if="selectedPengajuan.keterangan_penilaian">
            <h3 class="text-lg font-medium text-gray-900 mb-2">Keterangan</h3>
            <div class="bg-gray-50 p-4 rounded-lg">
              <p class="text-sm text-gray-900">{{ selectedPengajuan.keterangan_penilaian }}</p>
            </div>
          </div>

          <!-- Dokumen -->
          <div v-if="selectedPengajuan.file_kepersertaan_pengadaan && selectedPengajuan.file_kepersertaan_pengadaan.length > 0">
            <h3 class="text-lg font-medium text-gray-900 mb-2">Dokumen</h3>
            <div class="bg-gray-50 p-4 rounded-lg max-w-2xl">
              <div class="space-y-2">
                <div v-for="file in selectedPengajuan.file_kepersertaan_pengadaan" :key="file.id"
                     class="flex items-center p-2 hover:bg-gray-100 rounded-lg group">
                  <div class="flex-grow min-w-0">
                    <span class="text-sm text-gray-900 font-medium block truncate">{{ file.file_kepersertaan_pengadaan_type?.nama || 'Dokumen' }}</span>
                    <span class="text-sm text-gray-500 block truncate">{{ file.nama_file }}</span>
                  </div>
                  <a :href="route('file.download_file_kepersertaan', file.id)"
                     class="ml-4 shrink-0 inline-flex items-center px-2.5 py-1.5 text-sm text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-md"
                     download>
                    <svg class="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V3"/>
                    </svg>
                    Download
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #footer>
        <button
          @click="closePenilaianModal"
          class="inline-flex items-center px-4 py-2 bg-gray-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-gray-700 focus:outline-none focus:border-gray-700 focus:ring focus:ring-gray-200 active:bg-gray-600 disabled:opacity-25 transition"
        >
          <svg class="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Kembali
        </button>
      </template>
    </dialog-modal>

    <!-- BAEP Modal -->
    <dialog-modal :show="baepModalOpen" @close="closeBaepModal" :max-width="'7xl'" :closeable="false">
      <template #title>
        Berita Acara Evaluasi Penawaran
      </template>

      <template #content>
        <BeritaAcaraEvaluasiPenawaran
          :dokumen_pengadaan_id="dokumen.id"
        />
      </template>

      <template #footer>
        <div class="flex justify-end space-x-4">
          <button
            @click="closeBaepModal"
            class="inline-flex items-center px-4 py-2 bg-gray-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-gray-700 focus:outline-none focus:border-gray-700 focus:ring focus:ring-gray-200 active:bg-gray-600 disabled:opacity-25 transition"
          >
            <svg class="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
            </svg>
            Kembali
          </button>
        </div>
      </template>
    </dialog-modal>

    <!-- BAHP Modal -->
    <dialog-modal :show="bahpModalOpen" @close="closeBahpModal" :max-width="'7xl'" :closeable="false">
      <template #title>
        Berita Acara Hasil Penawaran
      </template>

      <template #content>
        <BeritaAcaraHasilPenawaran
          :dokumen_pengadaan_id="dokumen.id"
        />
      </template>

      <template #footer>
        <div class="flex justify-end space-x-4">
          <button
            @click="closeBahpModal"
            class="inline-flex items-center px-4 py-2 bg-gray-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-gray-700 focus:outline-none focus:border-gray-700 focus:ring focus:ring-gray-200 active:bg-gray-600 disabled:opacity-25 transition"
          >
            <svg class="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
            </svg>
            Kembali
          </button>
        </div>
      </template>
    </dialog-modal>

    <!-- Konfirmasi Selesaikan Modal -->
    <dialog-modal :show="confirmSelesaiOpen" @close="closeConfirmSelesai" :max-width="'lg'" :closeable="false">
      <template #title>
        Konfirmasi Selesaikan Dokumen
      </template>

      <template #content>
        <div class="space-y-3">
          <p class="text-sm text-gray-700">
            Tindakan ini akan menandai dokumen pengadaan sebagai selesai. Pastikan semua data sudah benar.
          </p>
          <div class="flex items-center p-3 bg-yellow-50 border border-yellow-200 rounded">
            <i class="pi pi-exclamation-triangle text-yellow-600 mr-2"></i>
            <span class="text-sm text-yellow-800">Konfirmasi dapat dilakukan dalam {{ confirmCountdown }} detik...</span>
          </div>
        </div>
      </template>

      <template #footer>
        <div class="flex justify-end space-x-2">
          <button
            @click="closeConfirmSelesai"
            class="inline-flex items-center px-4 py-2 bg-gray-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-gray-700 focus:outline-none focus:border-gray-700 focus:ring focus:ring-gray-200 active:bg-gray-600 disabled:opacity-25 transition"
          >
            Batal
          </button>
          <button
            :disabled="confirmCountdown > 0 || isSubmitting"
            @click="doSelesaikanDokumen"
            class="inline-flex items-center px-4 py-2 border border-transparent rounded-md font-semibold text-xs uppercase tracking-widest transition
                   text-white
                   disabled:opacity-25
                   "
            :class="(confirmCountdown > 0 || isSubmitting) ? 'bg-green-400' : 'bg-green-600 hover:bg-green-700 focus:border-green-700 focus:ring focus:ring-green-200 active:bg-green-600'"
          >
            <span v-if="isSubmitting" class="flex items-center">
              <svg class="-ml-1 mr-2 h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
              Memproses...
            </span>
            <span v-else>Ya, Selesaikan</span>
          </button>
        </div>
      </template>
    </dialog-modal>

    <Toast position="top-right" />
  </app-layout>
</template>

<script>
import { defineComponent } from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import DialogModal from '@/Jetstream/DialogModal.vue'
import Button from 'primevue/button'
import Toast from 'primevue/toast'
import { useToast } from 'primevue/usetoast'
import BeritaAcaraEvaluasiPenawaran from '@/Components/DokumenPengadaan/BeritaAcaraEvaluasiPenawaran.vue'
import BeritaAcaraHasilPenawaran from '@/Components/DokumenPengadaan/BeritaAcaraHasilPenawaran.vue'

export default defineComponent({
  components: {
    AppLayout,
    DialogModal,
    Button,
    Toast,
    BeritaAcaraEvaluasiPenawaran,
    BeritaAcaraHasilPenawaran,
  },

  props: {
    dokumen: {
      type: Object,
      required: true,
    },
    pengajuan: {
      type: Array,
      required: true,
    },
    allowSelesaikanNow: {
      type: Boolean,
      required: false,
      default: false,
    },
    allowSelesaikanAt: {
      type: [String, Date, Number],
      required: false,
      default: null,
    },
    evaluasiUlangHistory: {
      type: Object,
      required: false,
      default: null,
    },
  },

  setup() {
    const toast = useToast();
    return { toast }
  },

  data() {
    return {
      hpsModalOpen: false,
      penilaianModalOpen: false,
      selectedPengajuan: null,
      baepModalOpen: false,
      bahpModalOpen: false,
      confirmSelesaiOpen: false,
      confirmCountdown: 5,
      confirmTimer: null,
      isSubmitting: false,
    }
  },

  computed: {
    pemenang() {
      return this.pengajuan.find(p => p.is_pemenang) || null;
    },
    sortedPengajuan() {
      if (!Array.isArray(this.pengajuan)) return [];
      // Sort rules:
      // 1) Yang lulus semua dulu (semua penilaian is_lulus = true)
      // 2) Jika keduanya lulus semua, urutkan berdasarkan total nilai penawaran (terkecil dulu)
      return [...this.pengajuan].sort((a, b) => {
        const aLulus = this.isLulusSemua(a);
        const bLulus = this.isLulusSemua(b);

        if (aLulus && !bLulus) return -1;
        if (!aLulus && bLulus) return 1;

        if (aLulus && bLulus) {
          const aTotal = this.getTotalNilaiPenawaran(a) || 0;
          const bTotal = this.getTotalNilaiPenawaran(b) || 0;
          if (aTotal < bTotal) return -1;
          if (aTotal > bTotal) return 1;
        }

        return 0;
      });
    }
  },

  methods: {
    goBack() {
      this.$inertia.visit(route('dokumen_pengadaan.list_dokumen_pengumuman_pemenang'))
    },

    goToSanggah() {
      this.$inertia.visit(route('sanggah.index', this.dokumen.id))
    },

    viewEvaluasiUlangHistory() {
      if (this.evaluasiUlangHistory && this.evaluasiUlangHistory.id) {
        const url = route('riwayat-evaluasi.view', {
          dokumenPengadaanId: this.dokumen.id,
          riwayatId: this.evaluasiUlangHistory.id
        });
        this.$inertia.visit(url);
      }
    },

    formatDate(dateString) {
      if (!dateString) return null;
      return new Date(dateString).toLocaleDateString('id-ID', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return '-';
      const d = new Date(dateTimeString);
      const datePart = d.toLocaleDateString('id-ID', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
      const timePart = d.toLocaleTimeString('id-ID', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      return `${datePart} ${timePart} WIB`;
    },

    formatCurrency(value) {
      if (value === null || value === undefined) return 'Rp 0';
      return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    },

    getTotalNilaiPenawaran(pengajuan) {
      if (!pengajuan.detail_penawaran_kepersertaan_pengadaan) return 0;
      return pengajuan.detail_penawaran_kepersertaan_pengadaan.reduce((sum, item) =>
        sum + (item.volume * item.harga_satuan), 0);
    },

    getTotalHPS() {
      if (!this.dokumen.detail_h_p_s_dokumen_pengadaan) return 0;
      return this.dokumen.detail_h_p_s_dokumen_pengadaan.reduce((sum, item) =>
        sum + (item.volume * item.harga_satuan), 0);
    },

    getTotalSelisih(pengajuan) {
      return this.getTotalNilaiPenawaran(pengajuan) - this.getTotalHPS();
    },

    getTotalPercentageDiff(pengajuan) {
      const totalHPS = this.getTotalHPS();
      if (totalHPS === 0) return 0;
      return ((this.getTotalNilaiPenawaran(pengajuan) - totalHPS) / totalHPS) * 100;
    },

    getPercentageDiff(penawaran, hps) {
      if (hps === 0) return 0;
      return ((penawaran - hps) / hps) * 100;
    },

    getTotalNilaiPenilaian(pengajuan) {
      if (!pengajuan.penilaian_penyedia_dokumen_pengadaan) return 0;
      return pengajuan.penilaian_penyedia_dokumen_pengadaan.reduce((total, penilaian) => {
        const nilai = penilaian.nilai || 0;
        const bobot = penilaian.bobot_penilaian?.persentase_bobot || 0;
        return total + (nilai * bobot / 100);
      }, 0);
    },

    isLulusSemua(pengajuan) {
      const list = pengajuan?.penilaian_penyedia_dokumen_pengadaan;
      if (!Array.isArray(list) || list.length === 0) return false;
      return list.every(p => p?.is_lulus === true);
    },

    showHPSModal() {
      this.hpsModalOpen = true;
    },

    closeHPSModal() {
      this.hpsModalOpen = false;
    },

    showPenilaianDetail(pengajuan) {
      this.selectedPengajuan = pengajuan;
      this.penilaianModalOpen = true;
    },

    closePenilaianModal() {
      this.penilaianModalOpen = false;
      this.selectedPengajuan = null;
    },

    showBaepModal() {
      this.baepModalOpen = true;
    },

    closeBaepModal() {
      this.baepModalOpen = false;
    },

    showBahpModal() {
      this.bahpModalOpen = true;
    },

    closeBahpModal() {
      this.bahpModalOpen = false;
    },

    openConfirmSelesai() {
      this.confirmSelesaiOpen = true;
      this.confirmCountdown = 5;
      if (this.confirmTimer) {
        clearInterval(this.confirmTimer);
        this.confirmTimer = null;
      }
      this.confirmTimer = setInterval(() => {
        if (this.confirmCountdown > 0) {
          this.confirmCountdown -= 1;
        } else {
          clearInterval(this.confirmTimer);
          this.confirmTimer = null;
        }
      }, 1000);
    },

    closeConfirmSelesai() {
      this.confirmSelesaiOpen = false;
      if (this.confirmTimer) {
        clearInterval(this.confirmTimer);
        this.confirmTimer = null;
      }
    },

    async doSelesaikanDokumen() {
      this.isSubmitting = true;
      try {
        const response = await axios.post(route('dokumen_pengadaan.selesaikan', this.dokumen.id));
        if (response?.data?.success) {
          this.toast.add({ severity: 'success', summary: 'Berhasil', detail: response.data.message || 'Dokumen diselesaikan', life: 3000 });
          this.closeConfirmSelesai();
          this.$inertia.visit(route('dokumen_pengadaan.list_dokumen_pengumuman_pemenang'));
        } else {
          this.toast.add({ severity: 'warn', summary: 'Gagal', detail: (response?.data?.message) || 'Gagal menyelesaikan dokumen', life: 4000 });
        }
      } catch (e) {
        this.toast.add({ severity: 'error', summary: 'Error', detail: e?.response?.data?.message || 'Terjadi kesalahan', life: 4000 });
      } finally {
        this.isSubmitting = false;
      }
    },
  }
})
</script>

<style scoped>
/* Additional styles for winner announcement */
.bg-gradient-to-r {
  background: linear-gradient(to right, var(--tw-gradient-stops));
}
</style> 
